import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Hook around category-copy endpoints.
 * - list() / generate() / revoke(id, isActive) scoped to a single user_category.
 * - redeem(code, targetUtId) works standalone (used by the import bar).
 */
export default function useCategoryCopyCodes(userCategoryId) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!userCategoryId) { setItems([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get('/custom/category-copy/list.php', { params: { user_category_id: userCategoryId } });
            setItems(data.data?.items || []);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        }
        setLoading(false);
    }, [userCategoryId]);

    useEffect(() => { load(); }, [load]);

    const generate = useCallback(async () => {
        const { data } = await api.post('/custom/category-copy/generate.php', { user_category_id: userCategoryId });
        await load();
        return data.data;
    }, [userCategoryId, load]);

    const revoke = useCallback(async (codeId, isActive) => {
        await api.post('/custom/category-copy/revoke.php', { code_id: codeId, is_active: isActive });
        await load();
    }, [load]);

    return { items, loading, error, load, generate, revoke };
}

export async function redeemCopyCode(code, targetUserThematiqueId) {
    const { data } = await api.post('/custom/category-copy/redeem.php', {
        code,
        target_user_thematique_id: targetUserThematiqueId,
    });
    return data.data;
}

// Génère un code de copie de TOUTE la thématique (toutes ses catégories).
// Le même redeem.php détecte le niveau et recopie l'ensemble.
export async function generateThematiqueCopyCode(userThematiqueId) {
    const { data } = await api.post('/custom/category-copy/generate.php', {
        user_thematique_id: userThematiqueId,
    });
    return data.data;
}

// Liste + révocation des codes de copie de niveau THÉMATIQUE.
export function useThematiqueCopyCodes(userThematiqueId) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!userThematiqueId) { setItems([]); return; }
        setLoading(true);
        try {
            const { data } = await api.get('/custom/category-copy/list.php', { params: { user_thematique_id: userThematiqueId } });
            setItems(data.data?.items || []);
        } catch { /* silencieux */ }
        setLoading(false);
    }, [userThematiqueId]);

    useEffect(() => { load(); }, [load]);

    const revoke = useCallback(async (codeId, isActive) => {
        await api.post('/custom/category-copy/revoke.php', { code_id: codeId, is_active: isActive });
        await load();
    }, [load]);

    return { items, loading, load, revoke };
}
